<template>
	<div class="d-flex align-center flex-column">
		<v-card width="100%" class="elevation-2">
			<!-- BARRA SUPERIOR DE ACCIONES -->
			<s-toolbar
				label="Guía de Materiales e Insumos para Cosecha"
				dark
				:color="'#8e8f91'"
				close
				@close="$emit('close')"
				class="mb-3"
			>
				<template v-slot:append>
					<v-btn fab x-small color="warning" class="mr-2" @click="downloadPdf(1)">
						<v-icon medium>mdi-printer</v-icon>
					</v-btn>
					<v-btn fab x-small color="info" class="mr-4" @click="downloadPdf(0)">
						<v-icon medium>mdi-download</v-icon>
					</v-btn>
				</template>
			</s-toolbar>

			<v-card-title class="py-2 px-4">
				<v-spacer></v-spacer>
				<v-btn small color="warning" class="mr-2 text-capitalize elevation-1" @click="downloadPdf(1)">
					<v-icon left small>mdi-printer</v-icon>
					Imprimir
				</v-btn>
				<v-btn small color="info" class="text-capitalize elevation-1" @click="downloadPdf(0)">
					<v-icon left small>mdi-download</v-icon>
					Descargar PDF
				</v-btn>
			</v-card-title>

			<hr class="mb-4" />

			<!-- VUE-HTML2PDF GENERADOR -->
			<vue-html2pdf
				:show-layout="true"
				:float-layout="false"
				:enable-download="false"
				:preview-modal="false"
				:filename="pdfFileName"
				:paginate-elements-by-height="1100"
				:pdf-quality="2"
				pdf-format="a4"
				pdf-orientation="landscape"
				pdf-content-width="100%"
				:manual-pagination="true"
				:htmlToPdfOptions="htmlToPdfOptions"
				@beforeDownload="beforeDownload($event)"
				ref="html2Pdf"
			>
				<section slot="pdf-content">
					<div class="pdf-landscape-sheet">
						<!-- CABECERA: LOGO, TÍTULO PRINCIPAL Y CONTROL DE CALIDAD ISO -->
						<div class="header-container">
							<div class="logo-box">
								<img
									v-if="logoBase64"
									:src="logoBase64"
									alt="Logo Sunshine"
									class="company-logo"
								/>
								<img
									v-else
									src="/static/logo_sp.png"
									alt="Logo Sunshine"
									class="company-logo"
								/>
							</div>
							<div class="title-box">
								<h1 class="main-title">GUÍA DE MATERIALES E INSUMOS PARA COSECHA</h1>
								<div class="correlative-tag font-weight-bold" style="font-size: 11pt; color: #1e293b; margin-top: 4px;">
									{{ formatCorrelative(item) }}
								</div>
							</div>
							<div class="iso-box">
								<div class="iso-row">
									<div class="iso-label">CÓDIGO&nbsp;&nbsp;&nbsp;:</div>
									<div class="iso-value">SIG-CA-AN-FR-014</div>
								</div>
								<div class="iso-row">
									<div class="iso-label">VERSIÓN&nbsp;&nbsp;&nbsp;:</div>
									<div class="iso-value">06</div>
								</div>
								<div class="iso-row">
									<div class="iso-label">Fecha de creación&nbsp;&nbsp;&nbsp;:</div>
									<div class="iso-value">04/10/2010</div>
								</div>
								<div class="iso-row">
									<div class="iso-label">Última revisión&nbsp;&nbsp;&nbsp;:</div>
									<div class="iso-value">16/02/2026</div>
								</div>
								<div class="iso-row">
									<div class="iso-label">Página&nbsp;&nbsp;&nbsp;:</div>
									<div class="iso-value">1 de 1</div>
								</div>
								<div class="iso-row">
									<div class="iso-label">Anexos&nbsp;&nbsp;&nbsp;:</div>
									<div class="iso-value">00</div>
								</div>
							</div>
						</div>

						<!-- TABLA DE METADATOS (2 BLOQUES DE 4 CAMPOS) -->
						<table class="meta-grid-table">
							<tbody>
								<tr>
									<td class="meta-label">Fecha</td>
									<td class="meta-value">{{ formatDate(item.CmvDepartureDate) }}</td>
									<td class="meta-label">Zona</td>
									<td class="meta-value">{{ item.CmvCityName || item.CmvCity || item.CinLocationName || '' }}</td>
								</tr>
								<tr>
									<td class="meta-label">Chofer</td>
									<td class="meta-value">{{ item.CmvDriver || item.CmvCarrier || '' }}</td>
									<td class="meta-label">Vehículo</td>
									<td class="meta-value">{{ formatVehicle(item) }}</td>
								</tr>
								<tr>
									<td class="meta-label">Capataz</td>
									<td class="meta-value">{{ item.CmvForeman || item.CmvSupervisor || '' }}</td>
									<td class="meta-label">Destino</td>
									<td class="meta-value font-weight-bold">{{ item.CmvLocationName || item.CmvLocation || '' }}</td>
								</tr>
								<tr>
									<td class="meta-label">Proveedor</td>
									<td class="meta-value">{{ item.CmvSupplierName || item.CmvSupplier || '' }}</td>
									<td class="meta-label">Guía</td>
									<td class="meta-value font-weight-bold">{{ item.CmvReferralGuide || '' }}</td>
								</tr>
							</tbody>
						</table>

						<!-- TABLA CENTRAL DE MATERIALES (SALIDA Y DEVOLUCIÓN) -->
						<table class="materials-grid-table">
							<thead>
								<tr>
									<th rowspan="2" class="th-col-material">MATERIAL</th>
									<th colspan="2" class="th-group-salida">Salida</th>
									<th colspan="2" class="th-group-salida">Salida</th>
									<th rowspan="2" class="th-col-total">Total</th>
									<th colspan="3" class="th-group-devolucion">Devolución</th>
									<th colspan="3" class="th-group-devolucion">Devolución</th>
								</tr>
								<tr>
									<th class="th-sub">Cant</th>
									<th class="th-sub">Color</th>
									<th class="th-sub">Cant</th>
									<th class="th-sub">Color</th>
									<th class="th-sub">Vacías</th>
									<th class="th-sub">Llenas</th>
									<th class="th-sub">Placa</th>
									<th class="th-sub">Vacías</th>
									<th class="th-sub">Llenas</th>
									<th class="th-sub">Placa</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, idx) in formattedRows" :key="idx">
									<td class="td-material">{{ row.material }}</td>
									<td class="td-number">{{ row.salidaCant1 }}</td>
									<td class="td-text">{{ row.salidaColor1 }}</td>
									<td class="td-number">{{ row.salidaCant2 }}</td>
									<td class="td-text">{{ row.salidaColor2 }}</td>
									<td class="td-number font-weight-bold">{{ row.total }}</td>
									<td class="td-number">{{ row.devVacias1 }}</td>
									<td class="td-number">{{ row.devLlenas1 }}</td>
									<td class="td-text">{{ row.devPlaca1 }}</td>
									<td class="td-number">{{ row.devVacias2 }}</td>
									<td class="td-number">{{ row.devLlenas2 }}</td>
									<td class="td-text">{{ row.devPlaca2 }}</td>
								</tr>
							</tbody>
						</table>

						<!-- CAJA DE OBSERVACIONES -->
						<div class="observations-container">
							<span class="obs-title">Observaciones:</span>
							<span class="obs-content">{{ item.CmvObservation || '' }}</span>
						</div>

						<!-- BLOQUE DE FIRMAS (4 SECCIONES) -->
						<table class="signatures-grid-table">
							<tbody>
								<tr>
									<!-- COL 1: RESPONSABLE DE TURNO -->
									<td class="sig-cell">
										<div class="sig-header">RESPONSABLE DE TURNO:</div>
										<div class="sig-line">
											<span>Firma:</span>
											<span class="underline-fill"></span>
										</div>
										<div class="sig-line mt-2">
											<span>NOMBRE:</span>
											<span class="underline-fill"></span>
										</div>
									</td>

									<!-- COL 2: CHOFER DE SALIDA -->
									<td class="sig-cell">
										<div class="sig-header">CHOFER DE SALIDA:</div>
										<div class="sig-line">
											<span>Firma:</span>
											<span class="underline-fill"></span>
										</div>
									</td>

									<!-- COL 3: CHOFERES DE DEVOLUCIÓN -->
									<td class="sig-cell">
										<div class="sig-header">CHOFERES DE DEVOLUCIÓN:</div>
										<div class="sig-line">
											<span>Firma:</span>
											<span class="underline-fill"></span>
										</div>
										<div class="sig-line mt-2">
											<span>NOMBRE:</span>
											<!-- Se deja vacío para llenado y firma física manual -->
											<span class="underline-fill"></span>
										</div>
									</td>

									<!-- COL 4: CHOFERES DE DEVOLUCIÓN -->
									<td class="sig-cell">
										<div class="sig-header">CHOFERES DE DEVOLUCIÓN:</div>
										<div class="sig-line">
											<span>Firma:</span>
											<span class="underline-fill"></span>
										</div>
										<div class="sig-line mt-2">
											<span>NOMBRE:</span>
											<span class="underline-fill"></span>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>
			</vue-html2pdf>
		</v-card>
	</div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import moment from "moment";
import ContainerMovementService from "@/services/Logistics/ContainerMovementService";

const COLOR_MAP = {
	1: "CELESTE",
	2: "GUINDA",
	3: "VERDE",
	4: "BLANCO",
	5: "GRIS",
	6: "OTRO LOGO",
	7: "CREMA",
	8: "COLORES VARIOS",
	9: "NEGRO",
	10: "AZUL",
};

export default {
	name: "PdfContainerMovement",
	components: {
		VueHtml2pdf,
	},
	props: {
		item: {
			type: Object,
			required: true,
			default: () => ({}),
		},
	},
	data() {
		return {
			logoBase64: null,
			detailsList: [],
			downloadMode: 0,
			htmlToPdfOptions: {
				margin: [0.35, 0.4, 0.35, 0.4],
				filename: `Guia_Materiales_${this.item?.CmvReferralGuide || this.item?.CmvID || "Cosecha"}.pdf`,
				pagebreak: { mode: ["css", "legacy"] },
				html2canvas: {
					useCORS: true,
					scale: 2.5,
					letterRendering: true,
				},
				jsPDF: {
					unit: "in",
					format: "a4",
					orientation: "landscape",
				},
			},
		};
	},
	computed: {
		pdfFileName() {
			const guide = this.item?.CmvReferralGuide ? `_${this.item.CmvReferralGuide}` : "";
			return `Guia_Materiales_Cosecha_${this.item?.CmvID || "Doc"}${guide}.pdf`;
		},
		hasDevolutionRegistered() {
			return Boolean(
				this.item?.CmvEntryDate ||
				Number(this.item?.CmvQuantityIn) > 0
			);
		},
		formattedRows() {
			const rows = [];
			const totalRowCount = 9;
			const details = (this.detailsList && this.detailsList.length > 0)
				? this.detailsList
				: (this.item?.Details && this.item.Details.length > 0)
					? this.item.Details
					: null;

			const licensePlate = (this.item?.CmvLicensePlate || "").trim();
			const returnPlate = (this.item?.CmvReturnLicensePlate || licensePlate || "").trim();

			if (details && details.length > 0) {
				details.forEach((d) => {
					const materialDesc = (d.CmtDescription || d.CmdContainerType || "").trim();
					const qtyOutVal = d.CmdQuantityOut != null && d.CmdQuantityOut !== "" ? Number(d.CmdQuantityOut) : null;
					const rawColor = d.CmdContainerColor;
					const mappedColor = COLOR_MAP[rawColor] || COLOR_MAP[Number(rawColor)] || "";
					const colorVal = (d.CmdContainerColorName || mappedColor || (isNaN(Number(rawColor)) ? rawColor : "") || "").trim();
					const qtyInVal = d.CmdQuantityIn != null && d.CmdQuantityIn !== "" ? Number(d.CmdQuantityIn) : null;

					if (materialDesc || (qtyOutVal != null && qtyOutVal > 0)) {
						rows.push({
							material: materialDesc,
							salidaCant1: qtyOutVal != null ? qtyOutVal : "",
							salidaColor1: colorVal,
							salidaCant2: "",
							salidaColor2: "",
							total: qtyOutVal != null ? qtyOutVal : "",
							devVacias1: qtyInVal != null && qtyInVal > 0 ? qtyInVal : "",
							devLlenas1: "",
							devPlaca1: qtyInVal != null && qtyInVal > 0 ? returnPlate : "",
							devVacias2: "",
							devLlenas2: "",
							devPlaca2: "",
						});
					}
				});
			} else {
				const materialDesc = (this.item?.CmtDescription || this.item?.CmvContainerTypeName || "").trim();
				const qtyOutVal = this.item?.CmvQuantityOut != null && this.item?.CmvQuantityOut !== "" ? Number(this.item.CmvQuantityOut) : null;
				const rawColor = this.item?.CmvContainerColor;
				const mappedColor = COLOR_MAP[rawColor] || COLOR_MAP[Number(rawColor)] || "";
				const colorVal = (this.item?.CmvContainerColorName || mappedColor || this.item?.CmtColorName || (isNaN(Number(rawColor)) ? rawColor : "") || "").trim();
				const qtyInVal = this.item?.CmvQuantityIn != null && this.item?.CmvQuantityIn !== "" ? Number(this.item.CmvQuantityIn) : null;

				if (materialDesc || (qtyOutVal != null && qtyOutVal > 0)) {
					rows.push({
						material: materialDesc,
						salidaCant1: qtyOutVal != null ? qtyOutVal : "",
						salidaColor1: colorVal,
						salidaCant2: "",
						salidaColor2: "",
						total: qtyOutVal != null ? qtyOutVal : "",
						devVacias1: qtyInVal != null && qtyInVal > 0 ? qtyInVal : "",
						devLlenas1: "",
						devPlaca1: qtyInVal != null && qtyInVal > 0 ? returnPlate : "",
						devVacias2: "",
						devLlenas2: "",
						devPlaca2: "",
					});
				}
			}

			// Generate empty rows to preserve A4 landscape layout aesthetics without inventing materials
			while (rows.length < totalRowCount) {
				rows.push({
					material: "",
					salidaCant1: "",
					salidaColor1: "",
					salidaCant2: "",
					salidaColor2: "",
					total: "",
					devVacias1: "",
					devLlenas1: "",
					devPlaca1: "",
					devVacias2: "",
					devLlenas2: "",
					devPlaca2: "",
				});
			}

			return rows;
		},
	},
	mounted() {
		this.loadLogoBase64();
		this.loadDetails();
	},
	methods: {
		async loadDetails() {
			if (!this.item || !this.item.CmvID) return;
			if (this.item.Details && this.item.Details.length > 0) {
				this.detailsList = this.item.Details;
				return;
			}
			try {
				const reqId = this.$store?.state?.app?.user?.id || 0;
				const res = await ContainerMovementService.getDetails(this.item.CmvID, reqId);
				if (res && res.data && Array.isArray(res.data)) {
					this.detailsList = res.data;
				}
			} catch (e) {
				console.error("Error loading movement details for PDF:", e);
			}
		},
		formatCorrelative(item) {
			if (!item) return "";
			const id = item.CmvID || 0;
			return "N° " + String(id).padStart(6, "0");
		},
		formatDate(dateVal) {
			if (!dateVal) return "";
			return moment(dateVal).format("DD/MM/YYYY");
		},
		formatVehicle(item) {
			if (!item) return "";
			let plate = item.CmvLicensePlate || "";
			if (item.CmvSecondaryLicensePlate) {
				plate += ` / ${item.CmvSecondaryLicensePlate}`;
			}
			return plate;
		},
		downloadPdf(mode = 0) {
			this.downloadMode = mode;
			if (this.$refs.html2Pdf) {
				this.$refs.html2Pdf.generatePdf();
			}
		},
		beforeDownload({ html2pdf, options, pdfContent }) {
			html2pdf()
				.set(options)
				.from(pdfContent)
				.toPdf()
				.get("pdf")
				.then((pdf) => {
					const totalPages = pdf.internal.getNumberOfPages();
					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();

					// Numeración de páginas discreta en la parte inferior
					for (let i = 1; i <= totalPages; i++) {
						pdf.setPage(i);
						pdf.setFontSize(7.5);
						pdf.setTextColor(120);
						pdf.text(
							`Página ${i} de ${totalPages}`,
							pageWidth - 0.45,
							pageHeight - 0.15,
							{ align: "right" }
						);
					}

					const fileName =
						options.filename ||
						`Guia_Materiales_Cosecha_${this.item?.CmvID || "Doc"}.pdf`;

					if (this.downloadMode === 1 && typeof pdf.autoPrint === "function") {
						pdf.autoPrint();
						const blobUrl = pdf.output("bloburl");
						window.open(blobUrl, "_blank");
					} else {
						pdf.save(fileName);
					}
				});

			return false;
		},
		async loadLogoBase64() {
			try {
				const response = await fetch("/static/logo_sp.png");
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => {
					this.logoBase64 = reader.result;
				};
				reader.readAsDataURL(blob);
			} catch (e) {
				console.warn("No se pudo cargar el logo base64:", e);
			}
		},
	},
};
</script>

<style scoped>
/* CONTENEDOR GENERAL DE PÁGINA HORIZONTAL A4 */
.pdf-landscape-sheet {
	font-family: Arial, Helvetica, sans-serif;
	width: 100%;
	max-width: 1040px;
	margin: 0 auto;
	padding: 10px 18px;
	box-sizing: border-box;
	color: #000000;
	background: #ffffff;
}

/* 1. CABECERA CON LOGO, TÍTULO Y CONTROL DE CALIDAD ISO */
.header-container {
	display: flex;
	align-items: center;
	margin-bottom: 8px;
}

.logo-box {
	flex: 0 0 160px;
	display: flex;
	align-items: center;
}

.company-logo {
	width: 140px;
	height: auto;
	object-fit: contain;
}

.title-box {
	flex: 1;
	text-align: center;
	padding: 0 8px;
}

.main-title {
	margin: 0;
	font-size: 15px;
	font-weight: 900;
	letter-spacing: 0.5px;
	text-transform: uppercase;
}

.iso-box {
	flex: 0 0 210px;
	font-size: 8.5px;
	text-align: center;
	border-bottom: 2px solid #000000;
	font-weight: bold;
	line-height: 12px;
	padding-bottom: 2px;
}

.iso-row {
	display: flex;
	gap: 2px;
}

.iso-label {
	flex: 1.4;
	text-align: end;
	white-space: nowrap;
}

.iso-value {
	flex: 1;
	text-align: start;
	white-space: nowrap;
}

/* 2. TABLA DE METADATOS (BORDES SÓLIDOS NEGROS) */
.meta-grid-table {
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #000000;
	margin-bottom: 8px;
	font-size: 10px;
}

.meta-grid-table td {
	border: 1px solid #000000;
	padding: 3px 6px;
	height: 19px;
	vertical-align: middle;
}

.meta-label {
	width: 80px;
	font-weight: bold;
	background-color: #f7f7f7;
}

.meta-value {
	width: calc(50% - 80px);
	text-transform: uppercase;
}

/* 3. TABLA CENTRAL DE MATERIALES (SALIDAS Y DEVOLUCIONES) */
.materials-grid-table {
	width: 100%;
	border-collapse: collapse;
	border: 1.5px solid #000000;
	margin-bottom: 6px;
	font-size: 9.5px;
	empty-cells: show;
}

.materials-grid-table th,
.materials-grid-table td {
	border: 1px solid #000000;
	padding: 2.5px 4px;
	text-align: center;
	height: 18px;
	vertical-align: middle;
}

.materials-grid-table thead th {
	font-weight: bold;
	background-color: #f4f4f4;
}

.th-col-material {
	width: 26%;
	text-align: center;
}

.th-group-salida {
	width: 16%;
}

.th-col-total {
	width: 6%;
}

.th-group-devolucion {
	width: 26%;
}

.th-sub {
	font-size: 8.5px;
	font-weight: bold;
}

.td-material {
	text-align: left;
	padding-left: 6px;
	font-size: 9px;
}

.td-number {
	text-align: center;
	font-size: 9.5px;
}

.td-text {
	text-align: center;
	font-size: 8.5px;
	text-transform: uppercase;
}

/* 4. CAJA DE OBSERVACIONES */
.observations-container {
	border: 1px solid #000000;
	padding: 4px 8px;
	min-height: 28px;
	margin-bottom: 8px;
	font-size: 10px;
	display: flex;
	align-items: flex-start;
}

.obs-title {
	font-weight: bold;
	margin-right: 6px;
}

.obs-content {
	flex: 1;
	word-break: break-word;
}

/* 5. TABLA DE FIRMAS (4 BLOQUES ALINEADOS) */
.signatures-grid-table {
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #000000;
	font-size: 9px;
}

.signatures-grid-table td {
	border: 1px solid #000000;
	padding: 6px 8px 10px 8px;
	vertical-align: top;
	width: 25%;
	height: 65px;
}

.sig-header {
	font-weight: bold;
	font-size: 9.5px;
	margin-bottom: 16px;
	text-transform: uppercase;
}

.sig-line {
	display: flex;
	align-items: flex-end;
	gap: 4px;
	font-size: 9px;
}

.underline-fill {
	flex: 1;
	border-bottom: 1px solid #000000;
	height: 12px;
}

.underline-text {
	text-align: center;
	font-size: 8.5px;
	font-weight: bold;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
